export type RefreshTimer = {
    timeoutId?: NodeJS.Timeout;
    previousRefreshTimeMs?: number;
    numberOfAttempts: number;
}

export enum TokenType {
    Anonymous,
    Host
}

export class TokenRefreshManager {
    private static readonly tokenExpirationBufferMs: number = 1000 * 60 * 4; // 4 minutes
    private static readonly tokenRefreshBackoffIntervalMs: number = 30000; // 30 seconds;
    private static readonly tokenRefreshMaximumAttempts: number = 3;

    private timers = new Map<TokenType, RefreshTimer>();

    /**
     * Schedules refresh of token with provided id and relative token expiration time, using provided fetch operation
     */
    public scheduleRefresh(tokenType: TokenType, tokenExpiresInSec: number, fetchOperation: () => void): void {
        let timer: RefreshTimer = this.timers.get(tokenType);
        if (!timer) {
            timer = { numberOfAttempts: 0 };
            this.timers.set(tokenType, timer);
        }

        if (timer.timeoutId) {
            clearTimeout(timer.timeoutId);
            timer.timeoutId = undefined;
        }

        let tokenExpirationOffset = tokenExpiresInSec * 1000 - TokenRefreshManager.tokenExpirationBufferMs;

        if (tokenExpirationOffset > 0) {
            timer.numberOfAttempts = 0;
        } else {
            // Offset being negative means that token is not longer valid and that we should schedule a refresh
            // We will increment number of attempts and adjust offset so that we do not attempt refresh more than once per tokenRefreshBackoffIntervalMs
            ++timer.numberOfAttempts;
            tokenExpirationOffset = TokenRefreshManager.tokenRefreshBackoffIntervalMs;
        }

        if (timer.numberOfAttempts <= TokenRefreshManager.tokenRefreshMaximumAttempts) {
            timer.timeoutId = setTimeout(() => fetchOperation(), tokenExpirationOffset);
        }
    }

    public clearTimeouts(): void {
        this.timers.forEach(timer => {
            clearTimeout(timer.timeoutId);
        });

        this.timers = new Map();
    }
}
